import Step from './Step'
import './From.scoped.scss'
import useRcsAvailableSenders from 'CampaignBuilder/hooks/useRcsAvailableSenders'

function FromShortDescriptor({ complete, senderType, senderName, rcsFallbackChannel, rcsFallbackSenderName, campaignType, selectedRcsSender }) {

  const entity = {
    sms_blast: 'text message',
    sms_survey: 'text survey',
    sms_conversations: 'text conversation',
  }[campaignType] || 'text message' 
  return (
    <div>
      {complete && (
        <p className="no-margin-bottom">
          {senderType === 'phone_number' && (
            <span>This {entity} will be sent from a SMS phone number.</span>
          )}
          {senderType === 'rcs' && <>
            <span>This {entity} will be sent via RCS{selectedRcsSender ? ` as "${selectedRcsSender.name}"` : ''}.</span>
            { rcsFallbackChannel === 'sms_phone' && (
              <span> If the recipient's phone does not support RCS, it will fall back to a SMS phone number.</span>
            )}
            { rcsFallbackChannel === 'sms_alphanumeric' && (
              <span> If the recipient's phone does not support RCS, it will fall back to a named sender <strong>{rcsFallbackSenderName}</strong>.</span>
            )}
          </>}
          {senderType === 'alphanumeric' && (
            <span>
              From: <span className="bold">{senderName}</span>
            </span>
          )}
        </p>
      )}
      {!complete && (
        <p className="incomplete no-margin-bottom">Who does the text come from?</p>
      )}
    </div>
  )
}

const From = () => {
  const dis = useDis()
  const { textMessageBlast, campaignType } = useSel((s) => s.campaign)
  const { senderName, senderType, rcsFallbackChannel, rcsFallbackSenderName, rcsMessagingServiceSid } = textMessageBlast
  const namedSendersCampaignAllowed = campaignType === 'sms_blast'
  const clientHasAlphanumericSenders = namedSendersCampaignAllowed && (window.ENV.SMS_SENDER_NAMES.length > 0 || window.ENV.CUSTOM_SMS_SENDER_NAMES_ENABLED)

  const [campaignRcsSenders, selectedRcsSender] = useRcsAvailableSenders(campaignType === 'sms_conversations' ? 'conversations' : 'messaging', rcsMessagingServiceSid)

  const validationErrors = (() => {
    let errors = []

    let senderNamesToTest = []
    if (senderType === 'alphanumeric') {
      senderNamesToTest.push(senderName)
    }
    if (senderType === 'rcs') {
      if (rcsFallbackChannel === 'sms_alphanumeric') {
        senderNamesToTest.push(rcsFallbackSenderName)
      }
      if (rcsMessagingServiceSid && !selectedRcsSender) {
        errors.push('The selected RCS sender is not valid.')
      }
    }

    senderNamesToTest.forEach(name => {
      if (!name) {
        errors.push('Sender name is required')
        return
      }
      if (window.ENV.SMS_SENDER_NAMES.includes(name)) {
        return
      }
      if (!window.ENV.CUSTOM_SMS_SENDER_NAMES_ENABLED) {
        errors.push('The sender name you entered is not registered. Please select a sender name from the dropdown or contact Movement support to register your custom sender name.')
        return
      }
      if (name.length > 11) {
        errors.push('Sender name cannot be longer than 11 characters')
      }
      if (!/[a-zA-Z]/.test(name)) {
        errors.push('Sender name must contain at least one letter')
      } else if (!/^[a-zA-Z0-9\s+\-_&]+$/.test(name)) {
        errors.push('Sender name can only contain letters, numbers, spaces, and the following special characters: + - _ &')
      }
    })
    return errors
  })()

  const complete = !!senderType && validationErrors.length === 0

  function renderPreview() {
    if (senderType === 'rcs') {
      return (
        <div className="rcs-preview">
          {selectedRcsSender?.logoUrl && <img src={selectedRcsSender.logoUrl} alt={selectedRcsSender.name || 'RCS'} className="rcs-logo" />}
          <span className="rcs-name">{selectedRcsSender?.name || 'RCS Sender'}</span>
        </div>
      )
    }

    return senderType === 'phone_number' ? (
      <span>You've received a message from 60108</span>
    ) : (
      <span>You've received a message from {senderName}</span>
    )
  }

  function updateTextMessageBlast(data) {
    dis({
      type: 'campaign/upsert',
      payload: {
        textMessageBlast: {
          ...textMessageBlast,
          ...data,
        },
      },
    })
  }


  return (
    <Step
      name="From"
      editor="from"
      complete={complete}
      canClickFinish={complete}
      validationErrors={validationErrors}
      shortDescriptor={
        <FromShortDescriptor
          complete={complete}
          senderType={senderType}
          senderName={senderName}
          rcsFallbackChannel={rcsFallbackChannel}
          rcsFallbackSenderName={rcsFallbackSenderName}
          campaignType={campaignType}
          selectedRcsSender={selectedRcsSender}
        />
      }
    >
      <div className="row">
        <div className="column two-thirds">
          <div className="radio-list">
            <FromPhoneNumberOption updateTextMessageBlast={updateTextMessageBlast}
              textMessageBlast={textMessageBlast} />

            {campaignRcsSenders.length > 0 && <FromRcsOption 
              updateTextMessageBlast={updateTextMessageBlast}
              textMessageBlast={textMessageBlast}
              campaignRcsSenders={campaignRcsSenders}
              />}

            {senderType === 'rcs' && <FromRcsOptionFallback
              updateTextMessageBlast={updateTextMessageBlast}
              textMessageBlast={textMessageBlast} 
              clientHasAlphanumericSenders={clientHasAlphanumericSenders}
              namedSendersCampaignAllowed={namedSendersCampaignAllowed} />}

            {namedSendersCampaignAllowed && <FromAlphanumericOption
              updateTextMessageBlast={updateTextMessageBlast}
              textMessageBlast={textMessageBlast}
              clientHasAlphanumericSenders={clientHasAlphanumericSenders}
              />}

          </div>
          {!campaignRcsSenders.length && <div className="tip margin-top">
            <div className="icon">
              <SvgIconsTip />
            </div>
            <p>
              Get ready for RCS! RCS is a new way to send text messages branded with your organisation's name,
              logo, and colours. It also allows for rich media and interactivity, such as buttons and carousels.
              Get in touch with Movement support to start getting set up.
            </p>
          </div>}
        </div>
        <div className="column third">
          <div className="phone">
            <SvgPhone />
            <div className="header">
              <span>Notifications</span>
            </div>
            <div className="preview">{renderPreview()}</div>
          </div>
        </div>
      </div>
    </Step>
  )
}

const FromPhoneNumberOption = ({ updateTextMessageBlast, textMessageBlast }) => {
  const { senderType } = textMessageBlast

  return <label
            className="radio"
            onClick={() => {
              updateTextMessageBlast({ senderType: 'phone_number' })
            }}
          >
            <div className="control">
              <input
                type="radio"
                name="sender"
                checked={senderType === 'phone_number'}
              />
            </div>
            <div className="label">
              <p className="bold">Phone number</p>
              <p className="thin">Use one of the phone numbers available to you.</p>
            </div>
          </label>
}

const FromRcsOption = ({updateTextMessageBlast, textMessageBlast, campaignRcsSenders}) => {
  const { senderType, rcsMessagingServiceSid } = textMessageBlast
  return <><label
        className="radio"
        onClick={() => {
          updateTextMessageBlast({ senderType: 'rcs' })
        }}
      >
        <div className="control">
          <input
            type="radio"
            name="sender"
            checked={senderType === 'rcs'}
          />
        </div>
        <div className="label">
          <p className="bold">RCS</p>
          <p className="thin">Send via RCS (branded sender).</p>
          <div className="field margin-top">
            {campaignRcsSenders.length > 1 && <select
              onChange={(e) => updateTextMessageBlast({ rcsMessagingServiceSid: e.target.value }) }
              value={rcsMessagingServiceSid}
              >
                {campaignRcsSenders.map((rcsSender) => (
                  <option key={rcsSender.messagingServiceSid} value={rcsSender.messagingServiceSid}>
                    {rcsSender.name}
                  </option>
                ))}
              </select>}
          </div>
        </div>
      </label>
      </>
}

const FromRcsOptionFallback = ({ updateTextMessageBlast, textMessageBlast, clientHasAlphanumericSenders, namedSendersCampaignAllowed }) => {
  const { rcsFallbackChannel, rcsFallbackSenderName } = textMessageBlast
  const smsSenderNames = window.ENV.SMS_SENDER_NAMES || []
  const customSmsSenderNamesEnabled = window.ENV['CUSTOM_SMS_SENDER_NAMES_ENABLED']

  if (!namedSendersCampaignAllowed) {
    return <div className='margin-left margin-top margin-bottom'>
        <p>Not all phones can receive RCS messages yet. If the person's phone does not support RCS, then we will fallback to a SMS phone number.</p>
      </div>
  }

  return <div className='margin-left margin-top margin-bottom'>
        <p>Not all phones can receive RCS messages yet. If the person's phone does not support RCS, then we will fallback to SMS. Please choose between a SMS phone number or a named sender.</p>
        <div className="radio-list margin-left">
          <label
            className="radio"
            onClick={() => {
              updateTextMessageBlast({ rcsFallbackChannel: 'sms_phone' })
            }}
          >
            <div className="control">
              <input
                type="radio"
                name="rcs_fallback_channel"
                checked={rcsFallbackChannel === 'sms_phone'}
              />
            </div>
            <div className="label">
              <p className="bold">Fallback to a SMS phone number</p>
            </div>
          </label>
          <label
            className={`radio ${!clientHasAlphanumericSenders ? 'disabled' : ''}`}
            onClick={() => {
              if (!clientHasAlphanumericSenders) {
                return
              }

              updateTextMessageBlast({ rcsFallbackChannel: 'sms_alphanumeric' })
            }}
          >
            <div className="control">
              <input
                type="radio"
                name="rcs_fallback_channel"
                checked={rcsFallbackChannel === 'sms_alphanumeric'}
                disabled={!clientHasAlphanumericSenders}
              />
            </div>
            <div className="label">
              <p className={`bold ${!clientHasAlphanumericSenders ? 'disabled-text' : ''}`}>Fallback to a named sender</p>
              {!clientHasAlphanumericSenders && <p className="thin disabled-text">No named senders available</p>}
              {rcsFallbackChannel === 'sms_alphanumeric' && (
                <div className="field margin-top">
                  {smsSenderNames && <select
                    onChange={(e) => updateTextMessageBlast({ rcsFallbackSenderName: e.target.value }) }
                    value={rcsFallbackSenderName}
                    >
                      <option value="">Select a sender name</option>
                      {smsSenderNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>}
                  {customSmsSenderNamesEnabled && <div className="margin-top">
                    <p>Or enter a custom sender name:</p>
                    <input
                      type="text"
                      className="margin-top half full-width"
                      value={rcsFallbackSenderName}
                      maxLength={11}
                      pattern="[a-zA-Z0-9\s+\-_&]+"
                      onInput={(e) => e.currentTarget.reportValidity()}
                      onChange={(e) => updateTextMessageBlast({ rcsFallbackSenderName: e.target.value })}
                      placeholder="Enter custom sender name"
                    />
                    </div>}
                </div>
              )}
            </div>
          </label>
        </div>
      </div>
}

const FromAlphanumericOption = ({ updateTextMessageBlast, textMessageBlast, clientHasAlphanumericSenders }) => {
  const { senderName, senderType } = textMessageBlast
  const smsSenderNames = window.ENV.SMS_SENDER_NAMES || []
  const customSmsSenderNamesEnabled = window.ENV['CUSTOM_SMS_SENDER_NAMES_ENABLED']

  return <label
        className={`radio ${!clientHasAlphanumericSenders ? 'disabled' : ''}`}
        onClick={() => {
          if (!clientHasAlphanumericSenders) {
            return
          }

        updateTextMessageBlast({ senderType: 'alphanumeric' })
      }}
    >
      <div className="control">
        <input
          type="radio"
          name="sender"
          checked={senderType === 'alphanumeric'}
          disabled={!clientHasAlphanumericSenders}
        />
      </div>
      <div className="label">
        <p className={`bold ${!clientHasAlphanumericSenders ? 'disabled-text' : ''}`}>Named sender</p>
        { clientHasAlphanumericSenders && <p className="thin">
          { !customSmsSenderNamesEnabled && 'You must contact Movement to register a sender name that is not listed here. This is due to regulatory requirements with mobile carriers.' }
          You cannot receive replies to a named sender.
        </p> }
        { !clientHasAlphanumericSenders && <p className="thin disabled-text">You do not have any registered named senders setup. Contact Movement support to set one up.</p> }

        {senderType === 'alphanumeric' && (
          <div className="field margin-top">
          {smsSenderNames && <select
            onChange={(e) => updateTextMessageBlast({ senderName: e.target.value }) }
            value={senderName}
            >
              <option value="">Select a sender name</option>
              {smsSenderNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>}
          {customSmsSenderNamesEnabled && <div className="margin-top">
            <p>Or enter a custom sender name:</p>
            <input
              type="text"
              className="margin-top half full-width"
              value={senderName}
              maxLength={11}
              pattern="[a-zA-Z0-9\s+\-_&]+"
              onInput={(e) => e.currentTarget.reportValidity()}
              onChange={(e) => updateTextMessageBlast({ senderName: e.target.value })}
              placeholder="Enter custom sender name"
            />
            </div>}
        </div>
        )}
      </div>
    </label>
}

export default From
